import Divider from '@mui/material/Divider'
import ListSubheader from '@mui/material/ListSubheader'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'

const DEFAULT_CHANNEL_NAME = 'stable'
const MANUAL_CHANNEL_NAME = 'manual'
const UPPERCASE_NAMES: Record<string, string> = { lts: 'LTS' }

interface Props {
  value: string | null
  channels: Channel[]
  onChange: (channelId: string | null) => void
  label?: string
  networkChannelId?: string | null
  disabled?: boolean
  /** A refused change, as one sentence. It replaces the help text until the next change. */
  error?: string | null
}

function displayName(s: string) {
  return UPPERCASE_NAMES[s] ?? s.charAt(0).toUpperCase() + s.slice(1)
}

export default function ChannelSelect({
  value,
  channels,
  onChange,
  label = 'Release',
  networkChannelId,
  disabled,
  error,
}: Props) {
  const isNode = networkChannelId !== undefined
  const defaultChannel = channels.find(ch => ch.name === DEFAULT_CHANNEL_NAME)
  const releaseChannels = channels.filter(ch => ch.name !== MANUAL_CHANNEL_NAME)
  const manualChannel = channels.find(ch => ch.name === MANUAL_CHANNEL_NAME)

  const effectiveChannel = isNode ? (channels.find(ch => ch.id === networkChannelId) ?? defaultChannel) : defaultChannel
  const defaultLabel = isNode
    ? `Default (${effectiveChannel ? displayName(effectiveChannel.name) : displayName(DEFAULT_CHANNEL_NAME)})`
    : `Default (${displayName(DEFAULT_CHANNEL_NAME)})`

  const helperText = isNode
    ? 'Controls which agent updates this node receives. "Default" follows the network setting, or the default channel for a node in no network.'
    : 'Controls which agent updates nodes in this network receive.'

  return (
    <TextField
      select
      fullWidth
      size="small"
      disabled={disabled}
      label={label}
      value={value ?? 'default'}
      onChange={e => onChange(e.target.value === 'default' ? null : e.target.value)}
      helperText={error || helperText}
      error={!!error}
    >
      <MenuItem value="default">{defaultLabel}</MenuItem>
      <Divider />
      <ListSubheader>Release Channel</ListSubheader>
      {releaseChannels.map(ch => (
        <MenuItem key={ch.id} value={ch.id}>
          {displayName(ch.name)}
        </MenuItem>
      ))}
      {manualChannel && [
        <Divider key="manual-divider" />,
        <ListSubheader key="manual-header">Update Policy</ListSubheader>,
        <MenuItem key={manualChannel.id} value={manualChannel.id}>
          Manual (no auto-updates)
        </MenuItem>,
      ]}
    </TextField>
  )
}
